import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';
import { today } from '@/pages/sites/pmis/components/format';
import { showRate, summaryAmount } from '@/pages/sites/pmis/office/officeDraft';
import { useOfficeDashboard, type OfficeDashboardRow } from '@/pages/sites/pmis/office/queries';

/**
 * 현장별 매출·원가 한 장 — 공무 대시보드({@link '/office/dashboard'})의 표를 홈으로 접는다.
 *
 * <p>홈에서 답해야 하는 질문은 대시보드보다 좁다 — <b>어느 현장이 새고 있나</b> 하나다. 그래서 아홉 열
 * 가운데 셋(매출·원가·원가율)만 남기고, 매출이 큰 순으로 다섯 줄만 세운다. 나머지는 '전체보기' 가 받는다.
 *
 * <p>숫자는 {@link rollupSite} 를 그대로 부른다. 위젯이 자기 셈을 가지면 홈과 대시보드가 다른 숫자를
 * 말하는 날이 오고, 그때 사람들은 <b>둘 다</b> 안 믿는다.
 *
 * <p>원가율은 언제나 <b>누계</b>다. 당월 원가율은 그 달에 기성이 몰렸는지에 따라 200% 도 30% 도 나와서,
 * 다섯 줄짜리 표에서는 경보가 아니라 잡음이 된다.
 *
 * <p>자료는 대시보드와 <b>같은 API</b>(`GET /office/dashboard`)에서 온다. 위젯이 자기 질의를 가지면
 * 같은 화면 안에서 두 숫자가 갈리고, 그때 사람들은 둘 다 안 믿는다.
 */

const TOP_N = 5;

type Scope = 'month' | 'cum';

export function OfficeSummaryWidget() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<Scope>('month');
  const todayDate = today();

  const { data, isPending, error } = useOfficeDashboard(
    'month',
    Number(todayDate.slice(0, 4)),
    Number(todayDate.slice(5, 7)),
    'all',
  );

  // 값이 하나도 안 들어온 현장은 홈에서 뺀다 — 대시보드는 미입력을 세워야 하지만(합계가 틀리므로),
  // 홈 위젯은 '지금 새는 현장' 을 묻는 자리라 빈 줄이 다섯 칸을 먹으면 안 된다.
  const rows = useMemo(() => (data?.rows ?? []).filter((row) => row.hasData), [data]);

  const revenueOf = useCallback(
    (row: OfficeDashboardRow) => (scope === 'month' ? row.periodRevenue : row.cumRevenue),
    [scope],
  );
  const costOf = (row: OfficeDashboardRow) => (scope === 'month' ? row.periodCost : row.cumCost);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (revenueOf(b) ?? -Infinity) - (revenueOf(a) ?? -Infinity)),
    [rows, revenueOf],
  );
  const top = sorted.slice(0, TOP_N);
  const totals = data?.totals;

  const monthLabel = `${Number(todayDate.slice(5, 7))}월`;

  return (
    <WidgetFrame
      title="현장별 매출·원가"
      icon="chart"
      to="/office/dashboard"
      pending={isPending}
      error={error}
      empty={rows.length === 0}
      emptyText="아직 공무 시트에 들어온 값이 없습니다. 현장 공무 탭에서 채우면 여기에 모입니다."
      headerRight={
        <div className="flex items-center gap-1">
          <Toggle on={scope === 'month'} onClick={() => setScope('month')}>
            {monthLabel}
          </Toggle>
          <Toggle on={scope === 'cum'} onClick={() => setScope('cum')}>
            누계
          </Toggle>
        </div>
      }
    >
      {/* 네 열 모두 nowrap 이라 좁은 폭에서는 표가 카드를 뚫는다. 카드 안에서만 옆으로 밀리게 가둔다. */}
      <div className="overflow-x-auto">
      <table className="text-ui-sm" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Head align="left">현장</Head>
            <Head>매출</Head>
            <Head>원가</Head>
            <Head title="누계 기준">원가율</Head>
          </tr>
        </thead>
        <tbody>
          {top.map((row) => (
            <tr
              key={row.siteId}
              onClick={() => navigate(`/sites/pmis?site=${row.siteId}&tab=office`)}
              className="hover:bg-slate-50"
              style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
              title={`${row.siteName} 공무 탭 열기`}
            >
              <td style={{ ...cell, textAlign: 'left', maxWidth: 0 }}>
                <span className="block truncate" style={{ color: 'var(--text-secondary)' }}>
                  {row.siteName}
                </span>
              </td>
              <td style={cell}>{summaryAmount(revenueOf(row))}</td>
              <td style={cell}>{summaryAmount(costOf(row))}</td>
              <td
                style={{ ...cell, color: overTarget(row) ? '#b91c1c' : 'var(--text-secondary)', fontWeight: overTarget(row) ? 700 : 400 }}
                title={row.targetCostRate == null ? '' : `목표 ${row.targetCostRate}%`}
              >
                {showRate(row.costRate)}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
            <td style={{ ...cell, textAlign: 'left' }}>합계 {rows.length}곳</td>
            <td style={cell}>{summaryAmount((scope === 'month' ? totals?.periodRevenue : totals?.cumRevenue) ?? null)}</td>
            <td style={cell}>{summaryAmount((scope === 'month' ? totals?.periodCost : totals?.cumCost) ?? null)}</td>
            <td style={cell}>{showRate(totals?.costRate ?? null)}</td>
          </tr>
        </tbody>
      </table>
      </div>

      {rows.length > TOP_N && (
        <p className="text-ui-xs" style={{ color: 'var(--text-muted)', marginTop: '6px' }}>
          매출 상위 {TOP_N}곳만 보입니다 — 나머지 {rows.length - TOP_N}곳은 전체보기에서.
        </p>
      )}
    </WidgetFrame>
  );
}

function overTarget(row: OfficeDashboardRow): boolean {
  return row.costRate != null && row.targetCostRate != null && row.costRate > row.targetCostRate;
}

function Toggle({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="text-ui-xs"
      style={{
        padding: '2px 7px',
        borderRadius: '6px',
        border: `1px solid ${on ? 'var(--accent-light)' : 'var(--border)'}`,
        background: on ? 'var(--accent-bg)' : 'transparent',
        color: on ? 'var(--accent)' : 'var(--text-muted)',
        fontWeight: on ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function Head({ children, align = 'right', title }: { children: React.ReactNode; align?: 'left' | 'right'; title?: string }) {
  return (
    <th
      title={title}
      className="text-ui-xs"
      style={{ padding: '4px 6px', fontWeight: 600, color: 'var(--text-muted)', textAlign: align, whiteSpace: 'nowrap' }}
    >
      {children}
    </th>
  );
}

const cell = { padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap' } as const;
